import {Component, Expressions, Plugins} from '@packages/systems/wfdl';
import {
  Qualified,
  namesEqual,
  qualify,
  getPluginName,
  getName,
} from '@packages/systems/wfdl-Qualified';
import {DesignerStateType} from '@packages/systems/core/records/DesignerState';
import {UiNodeStateType} from '@packages/systems/core/records/UiNodeState';
import {getGuid} from '@packages/systems/core/utils/NativeIdUtils';
import {
  getCurrentComponentType,
  closestComponentRoot,
  isUserComponent,
} from '@packages/utilities/core-elements';

import {
  EElement,
  type Expression,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';

export const updateComponentRenderInComponentMap = (
  componentMap: Component.ComponentMap,
  componentType: Plugins.QualifiedElement,
  newComponentRender: Expression
): Component.ComponentMap => {
  const oldComponent = componentMap.get(componentType);
  if (!oldComponent) return componentMap;

  const oldComponentRender = Component.getRender(oldComponent);

  if (oldComponentRender === newComponentRender) {
    return componentMap;
  }

  const newComponent = Component.update(oldComponent, {
    render: newComponentRender,
  });
  return componentMap.set(componentType, newComponent);
};

export const updateElementInComponent = (
  componentMap: Component.ComponentMap,
  componentId: Component.ComponentName,
  elementId: string,
  updater: (dataFromElementPath: ExpressionElement) => ExpressionElement
): Component.ComponentMap => {
  const componentType = qualify(Plugins.name('__SitePlugin'), componentId);
  const currentComponent = componentMap.get(componentType);

  if (!currentComponent) return componentMap;

  const currentRender = Component.getRender(currentComponent);

  const nextRender = Expressions.updateElement(
    elementId,
    updater,
    currentRender
  );
  return updateComponentRenderInComponentMap(
    componentMap,
    componentType,
    nextRender
  );
};

export const updateSelectedElement = (
  {
    DesignerStore,
    UiNodeStore,
  }: {
    DesignerStore: DesignerStateType;
    UiNodeStore: UiNodeStateType;
  },
  updater: (dataFromElementPath: ExpressionElement) => ExpressionElement
) => {
  const componentMap =
    DesignerStore.stagedComponents || DesignerStore.components;
  const component = getCurrentComponentType(
    componentMap,
    UiNodeStore.focusedSymbolNativeIdPath
  );

  if (getPluginName(component) !== '__SitePlugin') return componentMap;

  const nativeId = UiNodeStore.selectedNodeNativeId;

  if (!nativeId) return componentMap;

  return updateElementInComponent(
    componentMap,
    getName(component),
    getGuid(nativeId),
    updater
  );
};

/* In very rare cases we might want to update component, that is not the focused one.
  Currently the only known case in which we want this to be done is calculating staging components for drag and drop functionality.
  For example, users are currently able to drag element from page to focused user component. In this case page component is not a focused one, but we need it to be updated in staging components

  This util will allow us to identify what is the root component for element by using liveExpressionTree that contains all user components in the expanded state
*/

export const updateOneOfComponents = (
  element: ExpressionElement,
  liveExpressionTree: ExpressionElement,
  components: Component.ComponentMap,
  updater: (arg1: Expression) => Expression
): Component.ComponentMap => {
  /* Find component in expression tree with expanded user components where data should be updated */
  const rootToUpdate = closestComponentRoot(
    element,
    EElement(liveExpressionTree),
    components
  );

  const componentType = qualify(
    Plugins.name('__SitePlugin'), // <- this should get replaced when we will have enriched version of the expand components, so we would be able to support Plugin Components
    Component.name(rootToUpdate ? rootToUpdate.id : 'page')
  );

  const rootComponent = components.get(componentType);

  if (!rootComponent) return components;
  /* we need to retrieve render for the component separately from rootToUpdate, as in rootToUpdate all nested user components are expanded and in componentMap renders they should be not*/
  const renderExpr = Component.getRender(rootComponent);

  return updateComponentRenderInComponentMap(
    components,
    componentType,
    updater(renderExpr)
  );
};

export const removeUserComponent = (
  components: Component.ComponentMap,
  id: Component.ComponentName
) =>
  id === 'page'
    ? components
    : components.delete(qualify(Plugins.name('__SitePlugin'), id));

/**
 * Ensures the given name is unique among existing names.
 * If a conflict exists, appends an incrementing suffix (e.g., "Name 2", "Name 3").
 * Smart about existing trailing numbers (e.g., "Name 5" → "Name 6" if conflicting).
 * Comparison is case-insensitive by default.
 */
function ensureUniqueName(
  baseName: string,
  existingNames: string[],
  options?: {caseInsensitive?: boolean}
): string {
  const caseInsensitive = options?.caseInsensitive ?? true;
  const normalize = (s: string) => (caseInsensitive ? s.toLowerCase() : s);
  const existingSet = new Set(existingNames.map(normalize));

  // If base name is already unique, return it
  if (!existingSet.has(normalize(baseName))) {
    return baseName;
  }

  // Check if base name ends with a number (e.g., "Component 1")
  const baseNumberMatch = baseName.match(/ (\d+)$/);
  let baseNumber = baseNumberMatch?.[1] ? parseInt(baseNumberMatch[1], 10) : 1;
  const baseString = baseNumberMatch ? baseName.replace(/ \d+$/, '') : baseName;

  let nameAttempt = `${baseString} ${baseNumber + 1}`;

  while (existingSet.has(normalize(nameAttempt))) {
    baseNumber++;
    nameAttempt = `${baseString} ${baseNumber + 1}`;
  }

  return nameAttempt;
}

/**
 * Collects all display names from components in the site plugin,
 * optionally excluding a specific component ID or library components.
 */
function collectExistingDisplayNames(
  componentMap: Component.ComponentMap,
  options?: {excludeComponentId?: string; excludeLibraryComponents?: boolean}
): string[] {
  return componentMap.reduceAtPluginName(
    Plugins.name('__SitePlugin'),
    (names, comp, compId) => {
      if (
        options?.excludeComponentId &&
        compId === options.excludeComponentId
      ) {
        return names;
      }
      if (
        options?.excludeLibraryComponents &&
        Component.getOrigin(comp) !== undefined
      ) {
        return names;
      }
      const displayName = Component.getDisplayName(comp);
      if (displayName) {
        names.push(displayName);
      }
      return names;
    },
    [] as string[]
  );
}

/**
 * Checks if a `displayName` is already in use in any other User Component.
 * Ignores components with `origin` that were created from a Library.
 * If a `componentNameToIgnore` argument is passed, ignores that too.
 */
export const isComponentDisplayNameTaken = (
  displayName: string,
  components: Component.ComponentMap,
  componentNameToIgnore?: Qualified<Component.ComponentName>
) => {
  /*
   * If a Component is being edited,
   * we want to avoid including itself in this check
   */
  const shouldIgnoreComponent = (
    componentName: Qualified<Component.ComponentName>
  ) =>
    componentNameToIgnore && namesEqual(componentName, componentNameToIgnore);

  const existingComponent = components.find(
    (component, componentName) =>
      isUserComponent(componentName) &&
      !shouldIgnoreComponent(componentName) &&
      // We exclude components with `origin` that were
      // created from a Library and are readonly
      Component.getOrigin(component) === undefined &&
      Component.getDisplayName(component) === displayName
  );

  return existingComponent !== undefined;
};

/**
 * Generates a unique component name by appending a counter to the base name.
 * By default (forceIncrement: true), always generates a new name for duplication.
 * With forceIncrement: false, returns baseName if it's already unique.
 */
export const generateUniqueComponentName = (
  baseName: string,
  components: Component.ComponentMap,
  options?: {
    forceIncrement?: boolean;
    excludeComponentId?: string;
    caseInsensitive?: boolean;
  }
): string => {
  const forceIncrement = options?.forceIncrement ?? true;
  const existingNames = collectExistingDisplayNames(components, {
    excludeComponentId: options?.excludeComponentId,
    excludeLibraryComponents: true,
  });

  const namesToCheck = forceIncrement
    ? [...existingNames, baseName]
    : existingNames;

  return ensureUniqueName(baseName, namesToCheck, {
    caseInsensitive: options?.caseInsensitive ?? false,
  });
};
